import type { ValidationError } from '@/shared/exceptions/validation.error';

/** Minimal catalog shape for mapping attach validation messages to coverage rows. */
export type AttachValidationCatalogItem = {
  id: string;
  code: string;
  title: string;
};

export type MapAttachValidationToCoverageErrorsResult = {
  /** Coverage id → human-readable error message. */
  coverageErrors: Record<string, string>;
  /** Distinct coverage titles affected (for toast summaries). */
  coverageNames: string[];
  /** Message when the error cannot be tied to a specific coverage row. */
  generalMessage?: string;
};

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function formatEquivalentCoverageMessage(
  message: string,
  catalog: AttachValidationCatalogItem[]
): string {
  const patterns = [
    /cobertura\s+(\d+).*?cobertura\s+(\d+)/i,
    /(\d{5})\s+.*?\s+(\d{5})/,
    /(exclusiv|proibid|conflict|equivalent)/i,
  ];

  for (const pattern of patterns) {
    const match = message.match(pattern);
    if (match?.[1] && match[2]) {
      const firstCoverage = catalog.find(c => c.code === match[1]);
      const secondCoverage = catalog.find(c => c.code === match[2]);
      if (firstCoverage && secondCoverage) {
        return `É proibida a contratação da cobertura ${secondCoverage.title} ao contratar a cobertura ${firstCoverage.title}`;
      }
    }
  }

  let result = message;
  for (const item of catalog) {
    const code = String(item.code ?? '').trim();
    if (!code) continue;
    result = result.replace(
      new RegExp(`\\b${escapeRegExp(code)}\\b`, 'g'),
      item.title
    );
  }
  return result;
}

function findCoverageForMessage(
  message: string,
  fieldKey: string,
  catalog: AttachValidationCatalogItem[]
): AttachValidationCatalogItem | undefined {
  const codeMatch = message.match(/cobertura\s+(\w+)/i);
  const coverageCode = codeMatch?.[1]?.trim();
  if (coverageCode) {
    const byCode = catalog.find(
      c => c.code === coverageCode || c.id === coverageCode
    );
    if (byCode) return byCode;
  }

  return catalog.find(c => c.code === fieldKey || c.id === fieldKey);
}

function collectCandidateMessages(error: ValidationError): string[] {
  const fromFieldErrors =
    Object.values(error.fieldErrors ?? {})
      .map(entry => String(entry?.message ?? '').trim())
      .filter(Boolean) ?? [];

  const fromMessages = (error.messages ?? [])
    .map(m => String(m ?? '').trim())
    .filter(Boolean);

  const detail = String(error.detail ?? '').trim();
  const detailMessage =
    detail &&
    detail !== 'Validation error' &&
    detail !== 'A proposta foi rejeitada pela validação'
      ? detail
      : '';

  return [...fromFieldErrors, ...fromMessages, detailMessage].filter(Boolean);
}

/**
 * Maps InsureMO attach-element validation errors (HTTP 422) to coverage row ids.
 * Intended for reuse across products; currently wired only in Capital Global.
 */
export function mapAttachValidationToCoverageErrors(
  error: ValidationError,
  catalog: AttachValidationCatalogItem[]
): MapAttachValidationToCoverageErrorsResult {
  const coverageErrors: Record<string, string> = {};
  const coverageNames: string[] = [];
  const unmappedMessages: string[] = [];

  const registerCoverageError = (
    coverage: AttachValidationCatalogItem,
    message: string
  ) => {
    coverageErrors[coverage.id] = message;
    if (!coverageNames.includes(coverage.title)) {
      coverageNames.push(coverage.title);
    }
  };

  for (const [fieldKey, fieldValue] of Object.entries(
    error.fieldErrors ?? {}
  )) {
    const rawMessage = String(fieldValue?.message ?? '').trim();
    if (!rawMessage) continue;

    const enrichedMessage = formatEquivalentCoverageMessage(
      rawMessage,
      catalog
    );
    const coverage = findCoverageForMessage(rawMessage, fieldKey, catalog);

    if (coverage) {
      registerCoverageError(coverage, enrichedMessage);
    } else {
      unmappedMessages.push(enrichedMessage);
    }
  }

  for (const message of collectCandidateMessages(error)) {
    if (Object.values(coverageErrors).includes(message)) continue;
    if (unmappedMessages.includes(message)) continue;

    const enrichedMessage = formatEquivalentCoverageMessage(message, catalog);
    const coverage = findCoverageForMessage(message, '', catalog);

    if (coverage && !coverageErrors[coverage.id]) {
      registerCoverageError(coverage, enrichedMessage);
    } else if (!coverage && !unmappedMessages.includes(enrichedMessage)) {
      unmappedMessages.push(enrichedMessage);
    }
  }

  const generalMessage =
    unmappedMessages.length > 0 ? unmappedMessages.join('\n') : undefined;

  return { coverageErrors, coverageNames, generalMessage };
}
